import { Component, ViewChild, TemplateRef, Input, Output, EventEmitter } from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import * as taxCalculator from 'src/app/supporting/taxCalculator';
import { LanguageManagementService } from 'src/service/language-management.service';
import { BillingDetailsSimple, KeyValueId, LocalService } from 'src/service/local.service';

@Component({
  selector: 'app-bill-taxes',
  templateUrl: './bill-taxes.component.html',
  styleUrl: './bill-taxes.component.scss'
})
export class BillTaxesComponent {
  @ViewChild('content_bill_taxes')
  content!: TemplateRef<any>;
  @Input() isOpenPopup: boolean = false;
  @Output() isOpenPopupChange = new EventEmitter();
  @Output() onClose = new EventEmitter();

  @Input() billedItems: BillingDetailsSimple;
  isDetailedTax = true;

  billedItemsFinal: BillingDetailsSimple;
  lstSectionConfigKeys: string[] = ["billing", "product_config"];
  businessTaxesIfProductTaxIsNotAvailable: KeyValueId[] = [];
  businessTaxesIfProductTaxIsNotAvailableDisount: KeyValueId[] = [];

  adjustmentAmount: number = 0; discountAmount: number = 0; serviceChargeAmount = 0;

  constructor(private offcanvasService: NgbOffcanvas, public localService: LocalService, private languageManagementService: LanguageManagementService) {
  }

  ngOnInit(): void {
    this.languageManagementService.ensureLanguageSectionKeys(this.lstSectionConfigKeys);
  }

  ngOnChanges(): void {
    if (this.isOpenPopup) {
      this.onLoad();
      this.offcanvasService.open(this.content, {
        position: 'end',
        backdrop: 'static',
        keyboard: false,
        animation: true,
        panelClass: 'popup_overlay_right',
        beforeDismiss: this.onBeforeDismiss
      });
    }
  }

  onBeforeDismiss(): boolean {
    this.isOpenPopup = false;
    if (this.isOpenPopupChange) {
      this.isOpenPopupChange.emit(this.isOpenPopup);
    }
    return true;
  }

  onCloseTrigger(event?: any) {
    this.onClose.emit(event);
    this.onBeforeDismiss();
    this.offcanvasService.dismiss();
  }

  onGetBusinessTaxesIfProductTaxIsNotAvailable(isDiscount: boolean): KeyValueId[] {
    if (!isDiscount && (!this.businessTaxesIfProductTaxIsNotAvailable || this.businessTaxesIfProductTaxIsNotAvailable.length == 0)) {
      const taxCategoryId = this.billedItemsFinal.products[0].details.v24;
      this.businessTaxesIfProductTaxIsNotAvailable = (this.billedItemsFinal.businessTaxes.find(x => x.key == taxCategoryId)?.values ?? []).filter(x => x.key != 'discount');;
    }

    if (isDiscount && (!this.businessTaxesIfProductTaxIsNotAvailableDisount || this.businessTaxesIfProductTaxIsNotAvailableDisount.length == 0)) {
      const taxCategoryId = this.billedItemsFinal.products[0].details.v24;
      this.businessTaxesIfProductTaxIsNotAvailableDisount = (this.billedItemsFinal.businessTaxes.find(x => x.key == taxCategoryId)?.values ?? []).filter(x => x.key == 'discount');
    }

    return isDiscount ? this.businessTaxesIfProductTaxIsNotAvailableDisount : this.businessTaxesIfProductTaxIsNotAvailable;
  }

  onLoad() {
    this.billedItemsFinal = JSON.parse(JSON.stringify(this.billedItems));
    this.billedItemsFinal.products = this.billedItemsFinal.products.filter(x => !(x.details.v7 && x.details.v7 != '' && x.details.v7 != '0'));
    this.isDetailedTax = true;
  }

  isSpecificTaxAvailableForProduct(): boolean {
    return taxCalculator.IsSpecificTaxAvailableForProduct(this.billedItemsFinal.products, []);
  }

  onGetBaseAmountAll(): number {
    return taxCalculator.GetBaseAmountAll(this.billedItemsFinal.products, false);
  }

  onSetServiceChargeAmount() {
    const serviceChargePercentage = this.billedItemsFinal.bill.v23 ? parseFloat(this.billedItemsFinal.bill.v23) : 0;
    if (serviceChargePercentage > 0) {
      const totalAmount = this.onGetBaseAmountAll();
      this.serviceChargeAmount = totalAmount * serviceChargePercentage / 100;
    }
  }

  onGetTaxableAmountAll(): number {
    return taxCalculator.GetTaxableAmountAll(this.billedItemsFinal.products, this.billedItemsFinal.isNotConsiderTax, this.billedItemsFinal.businessTaxes, false);
  }

  onGetTaxAmountAll(key: string, isDiscount: boolean): number {
    const taxableAmount = isDiscount ? undefined : this.onGetTaxableAmountAll();
    return taxCalculator.GetTaxAmountAll(this.billedItemsFinal.products, this.billedItemsFinal.isNotConsiderTax, this.billedItemsFinal.businessTaxes, key, taxableAmount, !isDiscount);
  }

  onGetTaxAmountIncludeTaxAll(): number {
    const rtn = taxCalculator.GetPriceIncludeTax(this.billedItemsFinal.products, this.billedItemsFinal.isNotConsiderTax, this.billedItemsFinal.businessTaxes);
    return rtn;
  }

  onGetBaseAmountForProduct(productId: string, batchId: string, measurementTypeStr: string): number {
    return taxCalculator.GetBaseAmountForProduct(this.billedItemsFinal.products, productId, batchId, measurementTypeStr);
  }

  onGetRequitredTaxForProduct(productId: string, businessTaxCategoryId: string, batchId: string, measurementTypeStr: string, isDiscount: boolean): KeyValueId[] {
    if (isDiscount) {
      return taxCalculator.GetRequiredTaxForProduct(taxCalculator.ConvertToProdTaxes(this.billedItemsFinal.products), productId, businessTaxCategoryId, measurementTypeStr, this.billedItemsFinal.businessTaxes, batchId, true);
    } else {
      return taxCalculator.GetRequiredTaxForProduct(taxCalculator.ConvertToProdTaxes(this.billedItemsFinal.products), productId, businessTaxCategoryId, measurementTypeStr, this.billedItemsFinal.businessTaxes, batchId);
    }
  }

  onGetTaxAmountForProduct(productId: string, businessTaxCategoryId: string, batchId: string, measurementTypeStr: string, key: string, isDiscount?: boolean): number {
    const taxes = taxCalculator.GetRequiredTaxForProduct(taxCalculator.ConvertToProdTaxes(this.billedItemsFinal.products), productId, businessTaxCategoryId, measurementTypeStr, this.billedItemsFinal.businessTaxes, batchId, isDiscount);
    const tax = taxes.find(x => x.key == key);
    if (isDiscount) {
      const baseAmount = taxCalculator.GetBaseAmountForProduct(this.billedItemsFinal.products, productId, batchId, measurementTypeStr);
      return tax && tax.value.toString().length > 0 ? (baseAmount * parseFloat(tax.value.toString().trim())) / 100 : 0;
    } else {
      const taxableAmount = this.onGetTaxableAmountForProductId(productId, batchId, measurementTypeStr);
      return tax && tax.value.toString().length > 0 ? (taxableAmount * parseFloat(tax.value.toString()) / 100) : 0;
    }
  }

  onGetTaxAmountIncludeTaxForProductId(productId: string, batchId: string, measurementTypeStr: string): number {
    return taxCalculator.GetTaxAmountIncludeTaxForProductId(this.billedItemsFinal.products, taxCalculator.ConvertToProdTaxes(this.billedItemsFinal.products), this.billedItemsFinal.isNotConsiderTax, this.billedItemsFinal.businessTaxes, productId, batchId, measurementTypeStr);
  }

  onGetTaxableAmountForProductId(productId: string, batchId: string, measurementTypeStr: string): number {
    return taxCalculator.GetTaxableAmountForProduct(this.billedItemsFinal.products, productId, batchId, measurementTypeStr, taxCalculator.ConvertToProdTaxes(this.billedItemsFinal.products), this.billedItemsFinal.isNotConsiderTax, this.billedItemsFinal.businessTaxes);
  }

  onGetPriceIncludeTax(): number {
    const totalAmount = taxCalculator.GetPriceIncludeTax(this.billedItemsFinal.products, this.billedItemsFinal.isNotConsiderTax, this.billedItemsFinal.businessTaxes);
    this.onSetAdjustmentAmount(totalAmount);
    this.onSetManualDiscountAmount();
    this.onSetServiceChargeAmount();
    return totalAmount + this.adjustmentAmount - this.discountAmount + this.serviceChargeAmount;
  }

  onSetAdjustmentAmount(totalAmount?: number) {
    if (!totalAmount) {
      totalAmount = taxCalculator.GetPriceIncludeTax(this.billedItemsFinal.products, this.billedItemsFinal.isNotConsiderTax, this.billedItemsFinal.businessTaxes);
    }
    const adjustmentAmount: number = this.billedItemsFinal.bill && this.billedItemsFinal.bill.v18 ? parseFloat(this.billedItemsFinal.bill.v18) : 0;
    this.onSetManualDiscountAmount();
    this.adjustmentAmount = adjustmentAmount > 0 ? adjustmentAmount : taxCalculator.GetAdjustmentAmount(totalAmount, this.discountAmount, this.billedItemsFinal.adjustmentFormulas, adjustmentAmount);
  }

  onSetManualDiscountAmount() {
    this.discountAmount = this.billedItemsFinal.bill.v20 ? parseFloat(this.billedItemsFinal.bill.v20) : 0;
  }

  onGetTitle(key: string, index: number): string {
    return this.billedItemsFinal.taxTitles.find(x => x.key == key).value.toString();
  }
}
